import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { articleDetailImages } from '@/mocks/articleImages';

export default function ArticlePage() {
  const { articleId } = useParams<{ articleId: string }>();
  const article = articles.find((a) => a.id === articleId);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 md:px-6 pt-24 pb-20 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
            <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
          </div>
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium">
            返回首页
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  const articleCategory = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
      jobTitle: article.authorTitle,
    },
    datePublished: article.publishDate,
    url: `https://xingshizixun.com/article/${article.id}`,
    publisher: {
      '@type': 'Organization',
      name: '性事咨询员',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: `${article.title} - 性事咨询员`,
    description: article.summary,
    url: `https://xingshizixun.com/article/${article.id}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '性事咨询员',
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webpageSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <Breadcrumb />
      <main className="max-w-3xl mx-auto px-4 md:px-6 pb-20">
        <article>
          <header className="pt-8 pb-6">
            {articleCategory && (
              <Link
                to={`/category/${articleCategory.slug}`}
                className="inline-block text-xs font-medium text-primary-600 bg-primary-100 px-3 py-1 rounded-full mb-4 hover:bg-primary-200 transition-colors"
              >
                {articleCategory.name}
              </Link>
            )}
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                <strong className="text-foreground-700 font-medium">{article.author}</strong>
              </span>
              <span className="text-foreground-300">|</span>
              <span className="text-foreground-500 text-xs">{article.authorTitle}</span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> {article.publishDate}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> 阅读约{article.readTime}
              </span>
            </div>
          </header>

          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={articleDetailImages[article.imageSeq]}
              alt={article.title}
              className="w-full object-cover"
              loading="lazy"
            />
          </div>

          <div
            className="prose-custom text-foreground-800 leading-relaxed text-[15px] space-y-4"
            dangerouslySetInnerHTML={{ __html: article.content
              .replace(/<strong>/g, '<strong class="font-semibold text-foreground-900 block mt-6 mb-2">')
              .replace(/<\/strong>/g, '</strong>')
              .split('\n\n').map(p => p.trim()).filter(Boolean).map(p => {
                if (p.startsWith('<strong')) return p;
                return `<p class="mb-4">${p}</p>`;
              }).join('\n')
            }}
          />

          <div className="mt-12 pt-8 border-t border-background-200/60">
            <div className="flex flex-wrap gap-3">
              <span className="text-sm text-foreground-500">标签：</span>
              <span className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700">两性健康</span>
              {articleCategory && (
                <span className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700">{articleCategory.name}</span>
              )}
              <span className="text-xs px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700">专业咨询</span>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="mt-14">
            <h3 className="font-heading text-xl font-bold text-foreground-900 mb-5">相关文章</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-200"
                >
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {ra.title}
                  </h4>
                  <p className="text-xs text-foreground-500 line-clamp-2">{ra.summary}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-14 p-6 rounded-xl bg-primary-100/50 border border-primary-200/40 text-center">
          <h4 className="font-heading text-lg font-semibold text-foreground-900 mb-2">需要专业帮助？</h4>
          <p className="text-sm text-foreground-600 mb-4">
            如果您正在经历类似的困扰，我们的专业顾问随时准备为您提供一对一的私密咨询
          </p>
          <Link
            to="/about#contact"
            className="inline-block whitespace-nowrap px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors"
          >
            预约免费初步评估
          </Link>
        </div>
      </main>
      <Footer />
    </>
  );
}